import React, { useEffect, useState } from 'react';
import { X, Star, ExternalLink, Phone, Navigation, Bookmark, Globe } from 'lucide-react';
import { getCommunityRatings, getRecScore, getPlaceReviews, createBookmark } from '../../lib/communityApi';
import { formatPriceTier, getCurrencyForCity } from '@wander/shared/utils/currencyUtils';

/**
 * PlaceDetailModal — Web mirror of mobile PlaceDetailScreen.
 *
 * Shows: photo/header, place name + location, scores (Google, community, rec),
 * price/type/category pills, actions (Website, Call, Directions), map embed,
 * Rate CTA, Community Reviews.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   placeName: string
 *   city?: string
 *   country?: string
 *   placeCategory?: 'dining' | 'activity' | 'hotel'
 *   placeData?: object            // item.placeData from itinerary (rating, website, phone, vicinity, photos, openingHours, priceLevel, types, placeId, lat, lng)
 *   item?: object                 // optional: the underlying activity/meal item for extra context
 *   onRate?: (payload) => void    // if provided, renders a prominent "Rate this place" CTA
 */

function ratingColor(r) {
  if (r == null) return '#9ca3af';
  if (r >= 8) return '#059669';
  if (r >= 6) return '#0d9488';
  if (r >= 4) return '#d97706';
  return '#dc2626';
}

function stopProp(e) { e.stopPropagation(); }

export default function PlaceDetailModal({
  open,
  onClose,
  placeName,
  city,
  country,
  placeCategory,
  placeData,
  item,
  onRate,
}) {
  const [loading, setLoading] = useState(true);
  const [communityRating, setCommunityRating] = useState(null);
  const [recScore, setRecScore] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [bookmarked, setBookmarked] = useState(false);

  const pd = placeData || {};
  const location = [city, country].filter(Boolean).join(', ');

  useEffect(() => {
    if (!open || !placeName) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [ratings, rec, placeReviews] = await Promise.all([
          getCommunityRatings({ placeName, city, category: placeCategory }).catch(() => null),
          getRecScore({ placeName, city, category: placeCategory }).catch(() => null),
          pd?.placeId ? getPlaceReviews(pd.placeId, 10, 0).catch(() => ({ reviews: [] })) : Promise.resolve({ reviews: [] }),
        ]);
        if (cancelled) return;
        if (Array.isArray(ratings) && ratings[0]?.avg_rating) {
          setCommunityRating({
            avgRating: ratings[0].avg_rating,
            count: ratings[0].review_count || 0,
          });
        } else if (ratings?.avgRating) {
          setCommunityRating(ratings);
        } else {
          setCommunityRating(null);
        }
        if (rec) setRecScore(rec);
        const list = Array.isArray(placeReviews) ? placeReviews : (placeReviews?.reviews || []);
        setReviews(list);
      } catch { /* swallow */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [open, placeName, city, placeCategory, pd?.placeId]);

  // Close on Esc
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const recScoreValue = recScore?.score != null ? parseFloat(recScore.score)
    : (recScore?.predicted_score != null ? parseFloat(recScore.predicted_score) : null);
  const commRating = communityRating?.avgRating != null ? parseFloat(communityRating.avgRating) : null;
  const commCount = communityRating?.count || 0;

  const photoUrl = pd?.photoUrl || pd?.photos?.[0]?.url || pd?.photos?.[0] || null;
  const address = pd?.address || pd?.vicinity || pd?.formattedAddress || null;
  const openNow = pd?.openingHours?.openNow;
  const weekdayText = pd?.openingHours?.weekdayText || pd?.openingHours?.weekday_text || null;

  const mapsQuery = encodeURIComponent(`${placeName || ''}${city ? ', ' + city : ''}`);
  const mapSrc = pd?.placeId
    ? `https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_EMBED_KEY || ''}&q=place_id:${pd.placeId}`
    : null;
  const mapFallbackSrc = `https://maps.google.com/maps?q=${mapsQuery}&z=14&output=embed`;

  const handleDirections = () => {
    window.open(`https://www.google.com/maps/search/?api=1&query=${mapsQuery}`, '_blank', 'noopener');
  };

  const handleBookmark = async () => {
    try {
      await createBookmark({
        placeName,
        city,
        country,
        placeCategory: placeCategory || 'activity',
      });
      setBookmarked(true);
    } catch { /* ignore */ }
  };

  if (!placeName) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Details for ${placeName}`}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
        onClick={stopProp}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
          <h2 className="flex-1 text-[15px] font-extrabold text-[#313143] truncate" title={placeName}>
            {placeName}
          </h2>
          <button
            onClick={handleBookmark}
            title="Bookmark"
            className={`w-8 h-8 rounded-full flex items-center justify-center border ${bookmarked ? 'bg-teal-50 border-teal-300' : 'border-gray-200 hover:bg-gray-50'}`}
          >
            <Bookmark className={`w-4 h-4 ${bookmarked ? 'text-teal-600 fill-teal-600' : 'text-gray-600'}`} />
          </button>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto p-5 space-y-4">
          {/* Photo */}
          {photoUrl && (
            <div className="-mx-5 -mt-5 mb-1">
              <img
                src={photoUrl}
                alt=""
                className="w-full h-48 object-cover"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </div>
          )}

          {/* Name + location */}
          <div>
            <div className="text-[22px] font-extrabold text-[#313143] leading-tight">{placeName}</div>
            {location && <div className="text-[12px] text-gray-500 mt-1">{location}</div>}
          </div>

          {/* Scores row */}
          {(pd?.rating || commRating != null || recScoreValue != null) && (
            <div className="grid grid-cols-3 gap-2">
              {pd?.rating != null && (
                <div className="rounded-xl border border-gray-200 bg-white p-3 flex flex-col items-center">
                  <div className="flex items-center gap-1 px-2 py-0.5 rounded-lg" style={{ backgroundColor: ratingColor(pd.rating * 2) + '18' }}>
                    <Star className="w-3.5 h-3.5" style={{ color: '#F59E0B' }} fill="#F59E0B" />
                    <span className="text-[14px] font-extrabold" style={{ color: ratingColor(pd.rating * 2) }}>{pd.rating}</span>
                  </div>
                  <span className="text-[10px] text-gray-500 mt-1 font-semibold">Google</span>
                  {pd.userRatingsTotal ? (
                    <span className="text-[9px] text-gray-400">{pd.userRatingsTotal.toLocaleString()} ratings</span>
                  ) : null}
                </div>
              )}
              {commRating != null && (
                <div className="rounded-xl border border-gray-200 bg-white p-3 flex flex-col items-center">
                  <div className="px-2 py-0.5 rounded-lg" style={{ backgroundColor: ratingColor(commRating) + '18' }}>
                    <span className="text-[14px] font-extrabold" style={{ color: ratingColor(commRating) }}>{commRating.toFixed(1)}</span>
                  </div>
                  <span className="text-[10px] text-gray-500 mt-1 font-semibold">arrivo Score</span>
                  {commCount ? <span className="text-[9px] text-gray-400">{commCount} reviews</span> : null}
                </div>
              )}
              {recScoreValue != null && (
                <div className="rounded-xl border border-gray-200 bg-white p-3 flex flex-col items-center">
                  <div className="px-2 py-0.5 rounded-lg" style={{ backgroundColor: '#0d948818' }}>
                    <span className="text-[14px] font-extrabold" style={{ color: '#0d9488' }}>{recScoreValue.toFixed(1)}</span>
                  </div>
                  <span className="text-[10px] text-gray-500 mt-1 font-semibold">Rec Score</span>
                  <span className="text-[9px] text-gray-400">For you</span>
                </div>
              )}
            </div>
          )}

          {/* Info pills */}
          <div className="flex flex-wrap gap-1.5">
            {pd?.priceLevel != null && (
              <span className="bg-gray-100 rounded-lg px-2 py-0.5 text-[11px] font-semibold text-gray-600">
                {/* B11 fix: use shared formatPriceTier so the currency symbol
                    matches the destination's local currency (¥ in Japan, € in
                    Europe, KSh in Kenya) instead of always showing $. */}
                {formatPriceTier(
                  '$'.repeat(pd.priceLevel || 1),
                  getCurrencyForCity(country || city || ''),
                )}
              </span>
            )}
            {pd?.types?.[0] && (
              <span className="bg-gray-100 rounded-lg px-2 py-0.5 text-[11px] font-semibold text-gray-600 capitalize">
                {String(pd.types[0]).replace(/_/g, ' ')}
              </span>
            )}
            {placeCategory && (
              <span className="bg-gray-100 rounded-lg px-2 py-0.5 text-[11px] font-semibold text-gray-600 capitalize">
                {placeCategory}
              </span>
            )}
            {openNow != null && (
              <span className={`rounded-lg px-2 py-0.5 text-[11px] font-semibold ${openNow ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                {openNow ? 'Open now' : 'Closed'}
              </span>
            )}
          </div>

          {/* Address */}
          {address && (
            <div className="text-[12px] text-gray-600">{address}</div>
          )}

          {/* Hours */}
          {Array.isArray(weekdayText) && weekdayText.length > 0 && (
            <details className="rounded-xl border border-gray-200 bg-white">
              <summary className="cursor-pointer px-3 py-2 text-[12px] font-semibold text-[#313143]">Hours</summary>
              <div className="px-3 pb-3 pt-1 space-y-0.5">
                {weekdayText.map((w, i) => (
                  <div key={i} className="text-[11px] text-gray-600">{w}</div>
                ))}
              </div>
            </details>
          )}

          {/* Action buttons */}
          <div className="flex gap-2">
            {pd?.website && (
              <a
                href={pd.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-[12px] font-semibold border-[1.5px] border-teal-500 text-teal-600 hover:bg-teal-50"
              >
                <Globe className="w-4 h-4" /> Website
              </a>
            )}
            {pd?.phone && (
              <a
                href={`tel:${pd.phone}`}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-[12px] font-semibold border-[1.5px] border-teal-500 text-teal-600 hover:bg-teal-50"
              >
                <Phone className="w-4 h-4" /> Call
              </a>
            )}
            <button
              onClick={handleDirections}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-[12px] font-semibold border-[1.5px] border-teal-500 text-teal-600 hover:bg-teal-50"
            >
              <Navigation className="w-4 h-4" /> Directions
            </button>
          </div>

          {/* Map */}
          <div className="rounded-xl overflow-hidden border border-gray-200">
            <iframe
              title={`Map of ${placeName}`}
              src={mapSrc || mapFallbackSrc}
              className="w-full h-48 block"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen=""
            />
          </div>

          {/* Rate this place CTA — only when caller wires onRate */}
          {onRate && (
            <button
              onClick={() => onRate({ placeName, city, country, placeCategory, placeData, item })}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-[13px] font-bold text-white bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 transition-all shadow-sm"
            >
              <Star className="w-4 h-4 fill-white" />
              Rate this place
            </button>
          )}

          {/* Insider tips — passed via item.insiderTips if the caller attaches them */}
          {Array.isArray(item?.insiderTips) && item.insiderTips.length > 0 && (
            <div className="rounded-xl p-3 border border-amber-100" style={{ background: 'rgba(245,158,11,0.04)' }}>
              <div className="text-[10px] font-extrabold uppercase tracking-wider text-amber-600 mb-2">Insider tips</div>
              {item.insiderTips.map((t, i) => (
                <div key={i} className="text-[12px] text-[#475569] leading-snug mb-1 last:mb-0">
                  {typeof t === 'string' ? t : t?.tip}
                </div>
              ))}
            </div>
          )}

          {/* Reviews */}
          {loading ? (
            <div className="text-center text-[12px] text-gray-400 py-4">Loading reviews…</div>
          ) : reviews.length > 0 ? (
            <div>
              <div className="text-[13px] font-bold text-[#313143] mb-2">Community Reviews</div>
              <div className="space-y-2">
                {reviews.slice(0, 5).map((r, i) => (
                  <div key={r.id || i} className="rounded-xl border border-gray-200 bg-white p-3">
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-[12px] font-semibold text-[#313143]">{r.author_name || 'Wanderer'}</div>
                      {r.user_rating != null && (
                        <div className="text-[12px] font-extrabold" style={{ color: ratingColor(r.user_rating) }}>
                          {parseFloat(r.user_rating).toFixed(1)}
                        </div>
                      )}
                    </div>
                    {r.review_text && (
                      <div className="text-[11px] text-gray-600 leading-snug line-clamp-3">{r.review_text}</div>
                    )}
                    {Array.isArray(r.tags) && r.tags.length > 0 && (
                      <div className="flex gap-1 mt-1.5 flex-wrap">
                        {r.tags.slice(0, 3).map((tag, j) => (
                          <span key={j} className="bg-gray-50 rounded-md px-1.5 py-0.5 text-[10px] text-gray-500">{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <div className="text-[12px] text-gray-500">No community reviews yet</div>
              <div className="text-[11px] text-gray-400 mt-0.5">Be the first to rate this place!</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
